import { closeX } from './popup.js';
import { el } from '../state/store.js';

/* A DATE PICKER, in European conventions.

   <input type="date"> was what the grant form used, and it renders whatever
   the BROWSER's locale says: month-first on a US profile, Sunday-first weeks,
   and a text format that changes under you depending on whose machine the app
   is open on. For a field whose whole job is "when does this licence run out"
   that is the wrong kind of clever — the answer has to read the same way every
   time, to everyone.

   So: weeks run Monday to Sunday, dates read dd.mm.yyyy, and times are 24h.
   None of it is negotiated with the host.

   Deliberately not a general-purpose component. It picks a DAY, it has an
   explicit "no expiry" answer because a licence without one is a real and
   common case, and it knows nothing about times of day. */

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
                'July', 'August', 'September', 'October', 'November', 'December'];

const pad = n => String(n).padStart(2, '0');

/** dd.mm.yyyy — the format, not the host's opinion of it. */
export function formatDate(d) {
  if (!d) return '';
  const x = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(+x)) return '';
  return pad(x.getDate()) + '.' + pad(x.getMonth() + 1) + '.' + x.getFullYear();
}

/** dd.mm.yyyy HH:MM, 24-hour. For "last seen" columns. */
export function formatDateTime(d) {
  if (!d) return '';
  const x = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(+x)) return '';
  return formatDate(x) + ' ' + pad(x.getHours()) + ':' + pad(x.getMinutes());
}

/** YYYY-MM-DD in LOCAL time. toISOString() would shift the day for anyone
 *  east or west of UTC, which is exactly the off-by-one that makes a licence
 *  look like it expired a day early. */
export const toYMD = d =>
  d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate());

/* Monday-first. getDay() is Sunday-first (0=Sun), so this rotates it: the
   number of blank cells before the 1st. */
const leadingBlanks = first => (first.getDay() + 6) % 7;

const sameDay = (a, b) => a && b
  && a.getFullYear() === b.getFullYear()
  && a.getMonth() === b.getMonth()
  && a.getDate() === b.getDate();

/**
 * Build a month grid. Returns the element; `onPick(Date)` fires on a click.
 *
 * @param {Date|null} selected
 * @param {(d: Date) => void} onPick
 */
export function calendarGrid(selected, onPick) {
  const root = el('div', 'cal');
  // The month being LOOKED at, which is not the same as the one selected —
  // paging away from a chosen date must not deselect it.
  let view = new Date(selected || new Date());
  view.setDate(1);

  const head = el('div', 'cal__head');
  const prev = el('button', 'cal__nav', '‹');
  const next = el('button', 'cal__nav', '›');
  prev.type = next.type = 'button';
  const label = el('span', 'cal__month');
  head.append(prev, label, next);
  root.appendChild(head);

  const dow = el('div', 'cal__dow');
  DAYS.forEach(d => dow.appendChild(el('span', null, d)));
  root.appendChild(dow);

  const grid = el('div', 'cal__grid');
  root.appendChild(grid);

  function paint() {
    label.textContent = MONTHS[view.getMonth()] + ' ' + view.getFullYear();
    grid.textContent = '';
    const first = new Date(view.getFullYear(), view.getMonth(), 1);
    const days = new Date(view.getFullYear(), view.getMonth() + 1, 0).getDate();
    for (let i = 0; i < leadingBlanks(first); i++) grid.appendChild(el('span', 'cal__pad'));
    const today = new Date();
    for (let d = 1; d <= days; d++) {
      const when = new Date(view.getFullYear(), view.getMonth(), d);
      const b = el('button', 'cal__day', String(d));
      b.type = 'button';
      if (sameDay(when, today)) b.classList.add('is-today');
      if (sameDay(when, selected)) b.classList.add('is-on');
      /* A weekend is worth seeing at a glance when you are choosing a date
         somebody is expected to act on. */
      if (when.getDay() === 0 || when.getDay() === 6) b.classList.add('is-weekend');
      b.onclick = () => { selected = when; paint(); onPick(when); };
      grid.appendChild(b);
    }
  }
  const step = n => () => {
    view = new Date(view.getFullYear(), view.getMonth() + n, 1);
    paint();
  };
  prev.onclick = step(-1);
  next.onclick = step(1);

  paint();
  return root;
}

/**
 * The picker as a question: resolves to a YYYY-MM-DD string, to null for
 * "no expiry", or to undefined if it was dismissed.
 *
 * Three outcomes rather than two, and the difference between the last two is
 * the one that matters: clearing an expiry and changing your mind about
 * touching it at all must not be the same answer, or Cancel would silently
 * make every licence permanent.
 *
 * @param {{title?:string, value?:string|null, anchor?:HTMLElement}} opts
 * @returns {Promise<string|null|undefined>}
 */
export function pickDate({ title = 'Pick a date', value = null } = {}) {
  return new Promise(resolve => {
    const wrap = el('div', 'authwrap');
    const card = el('div', 'authcard authcard--cal');
    card.appendChild(el('h2', null, title));
    closeX(card, () => done(undefined));

    const chosen = value ? new Date(value + 'T12:00:00') : null;
    const read = el('p', 'authnote', chosen ? formatDate(chosen) : 'No expiry — runs until removed.');
    card.appendChild(read);

    let ymd = value;
    card.appendChild(calendarGrid(chosen, d => {
      ymd = toYMD(d);
      read.textContent = formatDate(d);
    }));

    const row = el('div', 'authrow authrow--split');
    const clear = el('button', 'btn btn--sm btn--gh', 'No expiry');
    clear.onclick = () => done(null);
    const right = el('div', 'btnrow');
    const cancel = el('button', 'btn btn--sm btn--gh', 'Cancel');
    cancel.onclick = () => done(undefined);
    const ok = el('button', 'btn btn--sm btn--pri', 'Set date');
    ok.onclick = () => done(ymd);
    right.append(cancel, ok);
    row.append(clear, right);
    card.appendChild(row);

    function done(v) {
      wrap.remove();
      resolve(v);
    }
    /* Escape dismisses. Every other overlay in this app is Close-button-only,
       which is defensible for a form you might have typed into — but this one
       holds nothing you could lose, and a dialog that traps you is worse. */
    /* Escape is ui/popup.js's now: it presses the X above, which lands in done. */
    /* Click-through on the backdrop, for the same reason. Guarded on the
       target being the backdrop ITSELF, so a click that merely started
       inside the card does not close it. */
    wrap.onclick = e => { if (e.target === wrap) done(undefined); };

    wrap.appendChild(card);
    document.body.appendChild(wrap);
  });
}
